import * as z from 'zod';
import client from '../client';

import { SEO, Localization } from '../strapiSchema';
import { blocksSchema } from '../blocksSchema';
import { blocks, seo, localizations } from '../strapiBlocks';
import { isNotFoundError, logMissingResource, PRIMARY_LOCALE } from '../strapiLocale';

const NewsPageZod = z.object({
  id: z.number(),
  documentId: z.string(),
  slug: z.string(),
  blocks: z.array(blocksSchema),
  preTitle: z.string(),
  title: z.string(),
  seo: SEO,
  localizations: z.array(Localization),
  locale: z.string(),
});

export type NewsPageType = z.infer<typeof NewsPageZod>;

const NewsPageSlugZod = NewsPageZod.pick({
  id: true,
  documentId: true,
  slug: true,
  seo: true,
});

export type NewsPageSlugType = z.infer<typeof NewsPageSlugZod>;

const getNewsPage = async (
  locale: string = PRIMARY_LOCALE,
): Promise<NewsPageType | null> => {
  try {
    const response = await client.get('/api/news-page', {
      params: {
        locale,
        populate: {
          blocks,
          seo,
          localizations,
        },
      },
    });

    if (!response.data?.data) {
      if (locale !== PRIMARY_LOCALE) {
        logMissingResource('news-page', locale);
        return null;
      }
      throw new Error('No news page found');
    }

    return NewsPageZod.parseAsync(response.data.data);
  } catch (error) {
    if (locale !== PRIMARY_LOCALE && isNotFoundError(error)) {
      logMissingResource('news-page', locale);
      return null;
    }

    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`API response do not match News Page structure : ${errorMessage}`);
  }
};

const getNewsPageSlug = async (
  locale: string = PRIMARY_LOCALE,
): Promise<NewsPageSlugType | null> => {
  try {
    const response = await client.get('/api/news-page', {
      params: {
        locale,
        fields: ['slug'],
        populate: {
          seo,
        },
      },
    });

    if (!response.data?.data) {
      if (locale !== PRIMARY_LOCALE) {
        logMissingResource('news-page', locale);
        return null;
      }
      throw new Error('No news page found');
    }

    return NewsPageSlugZod.parseAsync(response.data.data);
  } catch (error) {
    if (locale !== PRIMARY_LOCALE && isNotFoundError(error)) {
      logMissingResource('news-page', locale);
      return null;
    }

    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`API response do not match News Page structure : ${errorMessage}`);
  }
};

export {
  getNewsPage,
  getNewsPageSlug,
};
